import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone, PhoneForwarded, PhoneCall, Mail, Play, Pause, RotateCcw, ArrowRight,
  Sparkles, CircleCheck, Cpu, Moon, Menu, X, Headset, Users, Building2,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { EMPLOYEES, DEPARTMENTS } from "@/data/seed";

// Public marketing landing page (route "/"). It is illustrative only — it never
// places real calls or sends real email. "Explore the Demo" enters the live
// guest demo (the exact same flow the Sign-In page uses).

const EMP = Object.fromEntries(EMPLOYEES.map((e) => [e.id, e]));
const deptColor = (id) => (DEPARTMENTS.find((d) => d.id === id) || {}).color || "#00E5FF";

const STATUS = {
  available: { label: "Available", color: "#22C55E", Icon: CircleCheck },
  working: { label: "Working", color: "#8A2BE2", Icon: Cpu },
  off_duty: { label: "Off duty", color: "#64748B", Icon: Moon },
};

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.available;
  const { Icon } = s;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium"
      style={{ background: `${s.color}1A`, color: s.color, border: `1px solid ${s.color}55` }}
    >
      <Icon size={12} /> {s.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
function Header({ onExplore, onSignIn, onNav }) {
  const [open, setOpen] = useState(false);
  const links = [
    { label: "How It Works", id: "how-it-works", testId: "nav-how-it-works" },
    { label: "Meet Your Team", id: "meet-your-team", testId: "nav-meet-your-team" },
    { label: "Demo", id: "guided-demo", testId: "nav-demo" },
  ];
  const go = (id) => { setOpen(false); onNav(id); };
  return (
    <header className="sticky top-0 z-50 glass-strong border-b border-[#00E5FF]/15" data-testid="landing-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3.5 sm:px-8">
        <button onClick={() => onNav("top")} className="shrink-0" data-testid="header-logo" aria-label="IQ Associates home">
          <Logo size={34} withWordmark />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="header-nav">
          {links.map((l) => (
            <button key={l.id} onClick={() => go(l.id)} data-testid={l.testId}
              className="text-sm text-slate-300 transition-colors hover:text-white">
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <button onClick={onSignIn} data-testid="nav-signin-link"
            className="text-sm text-slate-300 transition-colors hover:text-white">Sign In</button>
          <button onClick={onExplore} data-testid="nav-explore-demo-button"
            className="rounded-xl bg-[#00E5FF] px-4 py-2 text-sm font-semibold text-[#030A16] transition-transform hover:-translate-y-0.5"
            style={{ boxShadow: "0 0 22px rgba(0,229,255,0.35)" }}>
            Explore the Demo
          </button>
        </div>

        <button onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle"
          className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 text-slate-200 lg:hidden" aria-label="Menu">
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-white/10 bg-[#0A1326]/95 lg:hidden" data-testid="mobile-menu">
            <div className="flex flex-col gap-1 px-5 py-4">
              {links.map((l) => (
                <button key={l.id} onClick={() => go(l.id)} data-testid={`${l.testId}-mobile`}
                  className="rounded-lg px-3 py-2.5 text-left text-sm text-slate-200 hover:bg-white/5">{l.label}</button>
              ))}
              <button onClick={() => { setOpen(false); onSignIn(); }} data-testid="nav-signin-link-mobile"
                className="rounded-lg px-3 py-2.5 text-left text-sm text-slate-200 hover:bg-white/5">Sign In</button>
              <button onClick={() => { setOpen(false); onExplore(); }} data-testid="nav-explore-demo-button-mobile"
                className="mt-1 rounded-xl bg-[#00E5FF] px-4 py-2.5 text-sm font-semibold text-[#030A16]">Explore the Demo</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
const HERO_GRID = [
  { id: "linda", activity: "Answering an inbound call", status: "available" },
  { id: "nora", activity: "Assisting a new client inquiry", status: "available" },
  { id: "ava", activity: "Drafting a client follow-up", status: "working" },
  { id: "miles", activity: "Coordinating a field dispatch", status: "working" },
  { id: "chris", activity: "Monitoring brand mentions", status: "available" },
  { id: "atlas", activity: "Verifying compliance logs", status: "off_duty" },
];

function Hero({ onExplore, onNav }) {
  return (
    <section id="top" className="relative scroll-mt-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-12 lg:gap-16">
        <motion.div className="lg:col-span-6" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#00E5FF]">Digital Headquarters for small business</span>
          <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Your next hire<br />is <span className="neon-text text-[#00E5FF]">AI.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base text-slate-300 sm:text-lg">
            AI associates that answer your business calls, transfer callers to the right associate, and send and receive email—so you can focus on running your business.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button onClick={() => onNav("guided-demo")} data-testid="hero-primary-cta"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#00E5FF] px-6 py-3.5 text-sm font-semibold text-[#030A16] transition-transform hover:-translate-y-0.5"
              style={{ boxShadow: "0 0 26px rgba(0,229,255,0.4)" }}>
              See Your Team in Action <ArrowRight size={16} />
            </button>
            <button onClick={onExplore} data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-6 py-3.5 text-sm font-semibold text-slate-100 transition-colors hover:border-[#00E5FF]/50 hover:text-white">
              <Sparkles size={15} /> Explore the Demo
            </button>
          </div>
          <p className="mt-4 text-xs text-slate-500" data-testid="hero-microcopy">No sign-in needed to explore.</p>
        </motion.div>

        <motion.div className="lg:col-span-6" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }}>
          <div className="glass-strong relative overflow-hidden rounded-3xl p-5 sm:p-6" data-testid="hero-associate-grid">
            <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.25), transparent 70%)" }} />
            <div className="mb-4 flex items-center justify-between">
              <div className="font-display text-sm font-semibold text-white">IQ Digital Headquarters</div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#FFBF00]/40 bg-[#FFBF00]/10 px-2.5 py-1 text-[11px] font-medium text-[#FFBF00]" data-testid="hero-sample-workflow-badge">
                <Sparkles size={11} /> Sample workflow
              </span>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {HERO_GRID.map((g, i) => {
                const e = EMP[g.id];
                const c = deptColor(e.department);
                return (
                  <motion.div key={g.id} data-testid={`hero-associate-${g.id}`}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.07 }}
                    className="flex items-center gap-3 rounded-2xl border border-white/8 bg-[#030A16]/50 p-3">
                    <img src={e.avatar} alt={e.name} className="h-12 w-12 shrink-0 rounded-xl object-cover" style={{ border: `2px solid ${c}`, boxShadow: `0 0 12px ${c}55` }} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-display text-sm font-semibold text-white">{e.name}</div>
                      <div className="truncate text-[11px] text-slate-400">{e.title}</div>
                      <div className="mt-1.5 flex items-center gap-1.5">
                        <StatusPill status={g.status} />
                      </div>
                      <div className="mt-1.5 truncate text-[11px] text-slate-500" title={g.activity}>{g.activity}</div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// How it works
// ---------------------------------------------------------------------------
const HOW = [
  { Icon: Headset, title: "Answer every call", color: "#00E5FF", desc: "A receptionist greets each caller by time of day, understands what they need, and never lets a call go unanswered." },
  { Icon: PhoneForwarded, title: "Route to the right associate", color: "#8A2BE2", desc: "Callers are transferred mid-call to the associate who handles their request — shipping, support, scheduling and more." },
  { Icon: Mail, title: "Send & receive email", color: "#38BDF8", desc: "An email-capable associate reads your support inbox, drafts replies in your voice, and sends them after review." },
];

function HowItWorks() {
  return (
    <section id="how-it-works" className="scroll-mt-24 border-t border-white/5">
      <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <div className="max-w-2xl">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#00E5FF]">How it works</span>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">Three things your AI team does every day</h2>
          <p className="mt-4 text-base text-slate-300">No scripts to write, no dashboards to babysit. Your associates handle the front line and keep you in the loop.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
          {HOW.map((h, i) => (
            <motion.div key={h.title} data-testid={`how-card-${i + 1}`}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="glass rounded-2xl border border-white/8 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#00E5FF]/40">
              <span className="grid h-12 w-12 place-items-center rounded-2xl" style={{ background: `${h.color}1A`, boxShadow: `0 0 22px ${h.color}33` }}>
                <h.Icon size={22} style={{ color: h.color }} />
              </span>
              <h3 className="mt-5 font-display text-xl font-semibold text-white">{h.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{h.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Guided demonstration (visitor-controlled storyboard)
// ---------------------------------------------------------------------------
const STEPS = [
  {
    key: "answer",
    title: "Reception answers the call",
    active: ["linda"],
    kind: "chat",
    lines: [
      { who: "Linda", text: "Good morning, thank you for calling ABC Services, my name is Linda, how can I help you today?" },
      { who: "Caller", text: "Hi — I'd like to set up a new service account." },
    ],
  },
  {
    key: "transfer",
    title: "Linda transfers to the right associate",
    active: ["linda", "nora"],
    kind: "transfer",
    lines: [
      { who: "Linda", text: "Happy to help — let me connect you with Nora in Customer Success. One moment." },
      { who: "Nora", text: "Hi, this is Nora from Customer Success — I hear you'd like to start a new account. I can help with that." },
    ],
  },
  {
    key: "email",
    title: "The associate sends a follow-up email",
    active: ["nora", "ava"],
    kind: "email",
    email: {
      from: "Ava · Administration",
      to: "new.client@email.com",
      subject: "Welcome to ABC Services — your account setup",
      body: "Hi there,\n\nThanks for calling in today. Here are the next steps to finish setting up your new service account, along with everything we discussed with Nora.\n\nWarm regards,\nAva\nABC Services",
    },
  },
];

function StepActors({ active }) {
  const ids = ["linda", "nora", "ava"];
  return (
    <div className="flex items-center justify-center gap-5 sm:gap-8" data-testid="demo-actors">
      {ids.map((id, idx) => {
        const e = EMP[id];
        const on = active.includes(id);
        const c = deptColor(e.department);
        return (
          <React.Fragment key={id}>
            <div className="flex flex-col items-center gap-2 text-center">
              <motion.img src={e.avatar} alt={e.name} animate={{ scale: on ? 1.06 : 1, opacity: on ? 1 : 0.45 }} transition={{ duration: 0.4 }}
                className="h-16 w-16 rounded-2xl object-cover sm:h-20 sm:w-20"
                style={{ border: `2px solid ${on ? c : "#334155"}`, boxShadow: on ? `0 0 22px ${c}88` : "none" }}
                data-testid={`demo-actor-${id}`} />
              <div>
                <div className="font-display text-xs font-semibold" style={{ color: on ? "#fff" : "#64748B" }}>{e.name}</div>
                <div className="text-[10px]" style={{ color: on ? c : "#475569" }}>{e.title}</div>
              </div>
            </div>
            {idx < ids.length - 1 && (
              <ArrowRight size={18} className="shrink-0" style={{ color: active.includes(ids[idx]) && active.includes(ids[idx + 1]) ? "#00E5FF" : "#334155" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function ChatBubbles({ lines }) {
  return (
    <div className="space-y-3" data-testid="demo-chat">
      {lines.map((l, i) => {
        const self = l.who !== "Caller";
        const e = EMP[l.who.toLowerCase()];
        const c = e ? deptColor(e.department) : "#94A3B8";
        return (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.25 }}
            className={`flex ${self ? "justify-start" : "justify-end"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${self ? "bg-white/5 text-slate-100" : "bg-[#00E5FF]/10 text-slate-100"}`}
              style={self ? { border: `1px solid ${c}44` } : { border: "1px solid rgba(255,255,255,0.08)" }}>
              <div className="mb-0.5 text-[10px] font-mono uppercase tracking-wide" style={{ color: self ? c : "#94A3B8" }}>{l.who}</div>
              {l.text}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

function EmailPreview({ email }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="rounded-2xl border border-white/10 bg-[#030A16]/60 p-5" data-testid="demo-email-preview">
      <div className="flex items-center gap-2 border-b border-white/8 pb-3">
        <Mail size={16} className="text-[#38BDF8]" />
        <span className="text-xs font-mono uppercase tracking-wide text-[#38BDF8]">Outgoing email · draft</span>
      </div>
      <dl className="mt-3 space-y-1 text-sm">
        <div className="flex gap-2"><dt className="w-16 shrink-0 text-slate-500">From</dt><dd className="text-slate-200">{email.from}</dd></div>
        <div className="flex gap-2"><dt className="w-16 shrink-0 text-slate-500">To</dt><dd className="text-slate-200">{email.to}</dd></div>
        <div className="flex gap-2"><dt className="w-16 shrink-0 text-slate-500">Subject</dt><dd className="font-medium text-white">{email.subject}</dd></div>
      </dl>
      <p className="mt-3 whitespace-pre-line border-t border-white/8 pt-3 text-sm leading-relaxed text-slate-300">{email.body}</p>
    </motion.div>
  );
}

function GuidedDemo({ onExplore }) {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    if (!playing) return;
    timer.current = setTimeout(() => setStep((s) => (s + 1) % STEPS.length), 4200);
    return () => clearTimeout(timer.current);
  }, [playing, step]);

  const cur = STEPS[step];
  const goStep = (i) => { setPlaying(false); setStep(i); };
  const replay = () => { setStep(0); setPlaying(true); };

  return (
    <section id="guided-demo" className="scroll-mt-24 border-t border-white/5 bg-[#060E1E]/40">
      <div className="mx-auto max-w-5xl px-5 py-20 sm:px-8 sm:py-28">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#FFBF00]/40 bg-[#FFBF00]/10 px-3 py-1 text-[11px] font-medium text-[#FFBF00]" data-testid="demo-illustrative-badge">
            <Sparkles size={11} /> Illustrative demonstration · no live calls or emails
          </span>
          <h2 className="mx-auto mt-5 max-w-3xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            One customer inquiry. A team that handles it.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-300">Step through a single call and watch Linda, Nora, and Ava hand it off seamlessly.</p>
        </div>

        <div className="glass-strong mt-12 rounded-3xl p-6 sm:p-8" data-testid="demo-stage">
          <StepActors active={cur.active} />

          <div className="mt-8 min-h-[220px]">
            <AnimatePresence mode="wait">
              <motion.div key={cur.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }}>
                <div className="mb-4 flex items-center gap-2">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-[#00E5FF] text-xs font-bold text-[#030A16]">{step + 1}</span>
                  <h3 className="font-display text-lg font-semibold text-white">{cur.title}</h3>
                </div>
                {cur.kind === "email" ? <EmailPreview email={cur.email} /> : <ChatBubbles lines={cur.lines} />}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controls */}
          <div className="mt-6 flex flex-col items-center gap-4 border-t border-white/8 pt-5 sm:flex-row sm:justify-between">
            <div className="flex items-center gap-2">
              <button onClick={() => setPlaying((p) => !p)} data-testid="demo-play-pause-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-[#00E5FF] px-4 py-2 text-sm font-semibold text-[#030A16] transition-transform hover:-translate-y-0.5">
                {playing ? <Pause size={15} /> : <Play size={15} />} {playing ? "Pause" : "Play"}
              </button>
              <button onClick={replay} data-testid="demo-replay-btn"
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-sm text-slate-200 transition-colors hover:border-[#00E5FF]/50 hover:text-white">
                <RotateCcw size={15} /> Replay
              </button>
            </div>
            <div className="flex items-center gap-2" data-testid="demo-step-controls">
              {STEPS.map((s, i) => (
                <button key={s.key} onClick={() => goStep(i)} data-testid={`demo-step-${i + 1}-btn`}
                  aria-label={`Go to step ${i + 1}`}
                  className="group flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium transition-colors"
                  style={{ background: i === step ? "rgba(0,229,255,0.12)" : "transparent", color: i === step ? "#00E5FF" : "#64748B", border: `1px solid ${i === step ? "rgba(0,229,255,0.4)" : "rgba(255,255,255,0.08)"}` }}>
                  <span className="h-2 w-2 rounded-full" style={{ background: i === step ? "#00E5FF" : "#334155" }} />
                  Step {i + 1}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 text-center">
          <p className="font-display text-lg text-white">Explore the demo and meet your AI team.</p>
          <button onClick={onExplore} data-testid="demo-launch-full-cta"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#00E5FF] px-6 py-3.5 text-sm font-semibold text-[#030A16] transition-transform hover:-translate-y-0.5"
            style={{ boxShadow: "0 0 26px rgba(0,229,255,0.4)" }}>
            <Sparkles size={15} /> Launch the Interactive Demo
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Meet your team
// ---------------------------------------------------------------------------
const ROLE_CARDS = [
  { id: "linda", RoleIcon: Headset, role: "Reception", color: "#F472B6", description: "Answers incoming calls, greets callers by time of day, and directs them to the right associate or department.", tags: ["Phone greeting", "Call transfer", "Routing"] },
  { id: "nora", RoleIcon: Users, role: "Customer Support", color: "#22D3EE", description: "Handles common questions using the business information you provide, and helps new customers get started.", tags: ["Q&A", "Onboarding", "Follow-up"] },
  { id: "ava", RoleIcon: Building2, role: "Administration", color: "#38BDF8", description: "Sends and receives business email from a shared inbox and prepares replies for your review.", tags: ["Email", "Drafts", "Back office"] },
];

const ROSTER = ["miles", "atlas", "chris", "sue", "matt", "jim"];

function MeetTeam({ onExplore }) {
  return (
    <section id="meet-your-team" className="scroll-mt-24 border-t border-white/5">
      <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <div className="max-w-2xl">
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-[#00E5FF]">Meet your team</span>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">AI associates for the roles that run your business</h2>
          <p className="mt-4 text-base text-slate-300">Start with the three front-line roles below — a full team of nine is ready in the demo.</p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
          {ROLE_CARDS.map((r, i) => {
            const e = EMP[r.id];
            return (
              <motion.div key={r.id} data-testid={`team-card-${r.id}`}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="glass-strong group flex flex-col rounded-3xl border border-white/8 p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-[#00E5FF]/40">
                <div className="flex items-center gap-4">
                  <img src={e.avatar} alt={e.name} className="h-20 w-20 rounded-2xl object-cover" style={{ border: `2px solid ${r.color}`, boxShadow: `0 0 20px ${r.color}55` }} />
                  <div>
                    <div className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wide" style={{ color: r.color }}>
                      <r.RoleIcon size={13} /> {r.role}
                    </div>
                    <div className="mt-1 font-display text-xl font-semibold text-white">{e.name}</div>
                    <div className="text-xs text-slate-400">{e.title}</div>
                  </div>
                </div>
                <p className="mt-5 flex-1 text-sm leading-relaxed text-slate-300">{r.description}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {r.tags.map((t) => (
                    <span key={t} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300">{t}</span>
                  ))}
                </div>
                <button onClick={onExplore} data-testid={`team-card-${r.id}-cta`}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors" style={{ color: r.color }}>
                  Meet {e.name} in the demo <ArrowRight size={15} />
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Full roster */}
        <div className="mt-12">
          <div className="mb-4 text-sm text-slate-400">Your full team of nine associates:</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="team-roster">
            {ROSTER.map((id) => {
              const e = EMP[id];
              const c = deptColor(e.department);
              return (
                <div key={id} data-testid={`team-roster-${id}`} className="flex flex-col items-center gap-2 rounded-2xl border border-white/8 bg-[#030A16]/40 p-4 text-center">
                  <img src={e.avatar} alt={e.name} className="h-14 w-14 rounded-xl object-cover" style={{ border: `2px solid ${c}66` }} />
                  <div className="font-display text-sm font-semibold text-white">{e.name}</div>
                  <div className="text-[11px] text-slate-400">{e.title}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Closing CTA + footer
// ---------------------------------------------------------------------------
function ClosingCTA({ onExplore, onSignIn }) {
  return (
    <section className="border-t border-white/5">
      <div className="mx-auto max-w-5xl px-5 py-20 sm:px-8 sm:py-24">
        <div className="glass-strong relative overflow-hidden rounded-3xl p-8 text-center sm:p-14" data-testid="closing-cta">
          <div className="pointer-events-none absolute -left-24 -top-24 h-64 w-64 rounded-full" style={{ background: "radial-gradient(circle, rgba(138,43,226,0.3), transparent 70%)" }} />
          <div className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.25), transparent 70%)" }} />
          <h2 className="relative font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">Ready to expand your team without the overhead?</h2>
          <p className="relative mx-auto mt-4 max-w-xl text-base text-slate-300">Explore the demo and meet your AI team — no sign-in needed.</p>
          <div className="relative mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button onClick={onExplore} data-testid="closing-cta-explore-demo"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#00E5FF] px-6 py-3.5 text-sm font-semibold text-[#030A16] transition-transform hover:-translate-y-0.5" style={{ boxShadow: "0 0 26px rgba(0,229,255,0.4)" }}>
              <Sparkles size={15} /> Explore the Demo
            </button>
            <button onClick={onSignIn} data-testid="closing-cta-signin"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-6 py-3.5 text-sm font-semibold text-slate-100 transition-colors hover:border-[#00E5FF]/50 hover:text-white">
              Sign In
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ onNav, onSignIn, onExplore }) {
  return (
    <footer className="border-t border-white/10 bg-[#030A16]" data-testid="footer">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-5 py-10 sm:px-8 md:flex-row">
        <div className="flex flex-col items-center gap-2 md:items-start">
          <Logo size={28} withWordmark />
          <div className="text-xs text-slate-500">Digital Headquarters for small business</div>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-5 text-sm text-slate-400">
          <button onClick={() => onNav("how-it-works")} className="hover:text-white" data-testid="footer-how-it-works">How It Works</button>
          <button onClick={() => onNav("meet-your-team")} className="hover:text-white" data-testid="footer-meet-your-team">Meet Your Team</button>
          <button onClick={onExplore} className="hover:text-white" data-testid="footer-demo">Demo</button>
          <button onClick={onSignIn} className="hover:text-white" data-testid="footer-signin">Sign In</button>
        </div>
      </div>
      <div className="border-t border-white/5 py-4 text-center text-xs text-slate-600">© {new Date().getFullYear()} IQ Associates · Illustrative product demo</div>
    </footer>
  );
}

// ---------------------------------------------------------------------------
export default function Landing() {
  const navigate = useNavigate();
  const exploreDemo = () => { try { localStorage.setItem("iq-guest", "1"); } catch (_) {} navigate("/hq"); };
  const goSignIn = () => navigate("/signin");
  const scrollTo = (id) => {
    if (id === "top") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#030A16] bg-radials grain text-white">
      <div className="bg-grid pointer-events-none absolute inset-0 opacity-30" />
      <div className="relative z-10">
        <Header onExplore={exploreDemo} onSignIn={goSignIn} onNav={scrollTo} />
        <main>
          <Hero onExplore={exploreDemo} onNav={scrollTo} />
          <HowItWorks />
          <GuidedDemo onExplore={exploreDemo} />
          <MeetTeam onExplore={exploreDemo} />
          <ClosingCTA onExplore={exploreDemo} onSignIn={goSignIn} />
        </main>
        <Footer onNav={scrollTo} onSignIn={goSignIn} onExplore={exploreDemo} />
      </div>
    </div>
  );
}
